import { useRef, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useVentures } from '../hooks/useVentures';

const STATUS_MAP = {
  'Autoform': 'In Development',
  'Spectra': 'In Development',
  'Signal': 'Launching 2026',
};

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
};

function VentureCard({ v, i }) {
  const videoRef = useRef(null);

  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = true;
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(() => {});
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (videoRef.current) videoRef.current.pause();
  }, []);

  const status = STATUS_MAP[v.title];

  return (
    <motion.div
      className={`venture-card ${v.size || 'medium'}`}
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.8, delay: 0.12 * i, ease: [0.16, 1, 0.3, 1] }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-testid={`venture-card-${i}`}
    >
      <Link to={`/ventures/${v.id}`} className="block w-full h-full">
        {v.img && (
          <img src={v.img} alt={v.title} loading="lazy" className="venture-poster" />
        )}
        {v.video && (
          <video
            ref={videoRef}
            className="venture-video"
            muted loop playsInline preload="metadata"
            poster={v.img}
            data-testid={`venture-video-${i}`}
          >
            <source src={v.video} type="video/mp4" />
          </video>
        )}
        <div className="venture-label">
          <div className="flex items-center gap-3">
            <h3>{v.title}</h3>
            {status && (
              <span className="venture-status" data-testid={`venture-status-${i}`}>{status}</span>
            )}
          </div>
          <span>{v.tag} &mdash; {v.year}</span>
        </div>
      </Link>
    </motion.div>
  );
}

export default function Ventures() {
  const { ventures, loading } = useVentures();
  const displayed = ventures.filter((v) => v.featured).slice(0, 4);
  const items = displayed.length > 0 ? displayed : ventures.slice(0, 4);

  return (
    <section id="ventures" className="py-24 md:py-36" data-testid="ventures-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="section-divider mb-12" />

        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12 md:mb-16">
          <div>
            <motion.p className="section-number" {...fadeUp} data-testid="ventures-number">
              Selected Work
            </motion.p>
            <motion.h2
              className="section-title mt-3"
              {...fadeUp}
              transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
              data-testid="ventures-title"
            >
              Ventures In
              <br />
              Progress
            </motion.h2>
          </div>
          <motion.p
            className="text-[var(--cv-text-dim)] text-sm max-w-[360px]"
            style={{ fontFamily: 'var(--font-body)', lineHeight: 1.7 }}
            {...fadeUp}
            transition={{ duration: 0.7, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            data-testid="ventures-subtitle"
          >
            Concepts and ventures we&apos;re actively shaping,
            building, or co-founding.
          </motion.p>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="preloader-spinner" />
          </div>
        ) : (
          <div className="venture-grid" data-testid="ventures-grid">
            {items.map((v, i) => (
              <VentureCard key={v.id} v={v} i={i} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
